<template>
    <div v-if="loading" class="text-center">
        <v-progress-circular indeterminate color="blue" size="100"></v-progress-circular>
    </div>
    <v-container v-else fluid :class="{ 'premium-page': isPremium }">
        <!-- Premium Banner -->
        <PremiumBanner :show="isPremium" label="Premium Subscription" :chip-text="premiumPlanTitle" />

        <!-- Last Job Execution Failed Banner -->
        <Message v-if="lastFailedExecution" severity="error" :closable="false" class="mb-4">
            <template #default>
                <div class="d-flex align-center flex-wrap" style="gap: 12px">
                    <span>
                        Last job execution
                        <router-link
                            :to="{
                                name: 'MonitorJobExecutionDetails',
                                params: { monitorId, jobId: lastFailedExecution.jobId, executionNumber: lastFailedExecution.executionNumber }
                            }"
                            ><strong>{{ lastFailedExecution.jobId }}</strong></router-link
                        >
                        {{ lastFailedExecution.status.toLowerCase().replace('_', ' ')
                        }}<template v-if="lastFailedExecution.lastUpdatedAtDate">
                            — {{ format(lastFailedExecution.lastUpdatedAtDate, 'M/d/yy h:mmaaaaa') }} (<time-ago
                                :date="lastFailedExecution.lastUpdatedAtDate"
                                :timezone-id="vesselTimezone"
                            />)
                        </template>
                    </span>
                    <span v-if="lastFailedExecution.statusDetails && Object.keys(lastFailedExecution.statusDetails).length">
                        —
                        {{
                            Object.entries(lastFailedExecution.statusDetails)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(', ')
                        }}
                    </span>
                </div>
            </template>
        </Message>

        <!-- Spa State Validator -->
        <SpaStateValidator
            v-if="vessel?.monitor?.monitorId"
            :account-id="accountId"
            :monitor-id="vessel.monitor.monitorId"
            :vessel-settings="vesselSettings"
            :upcoming-activity="upcomingActivity"
            :vessel-timezone="vesselTimezone"
            @health="spaHealth = $event"
        />
        <div v-if="vessel" class="vessel-layout mt-2 mb-4">
            <!-- Persistent left sidebar: status summary + health checks, shown across all tabs -->
            <aside class="vessel-sidebar">
                <!-- Vessel Status Summary -->
                <div class="vessel-status-summary">
                    <div class="status-header" :class="vesselStatusClass">
                        <div class="status-temp" v-if="vessel.status?.discElements?.tempC">{{ tempCToTempF(vessel.status.discElements.tempC) }}°</div>
                        <div class="status-name">{{ vessel.name }}</div>
                        <div class="status-actions-count" v-if="vessel.status?.discElements?.text">
                            {{ vessel.status.discElements.text }}
                        </div>
                    </div>
                    <v-btn
                        v-if="showEngineeringOptions"
                        color="red"
                        icon
                        size="x-small"
                        class="mt-2"
                        @click="showUpdateStatusDialog = true"
                        title="Edit Status"
                    >
                        <v-icon icon="mdi-database-edit" color="white" size="16" />
                    </v-btn>
                    <v-chip v-if="vessel.states.waitForNextSample" class="mt-2" color="orange" variant="outlined" size="small">Waiting for Sample</v-chip>
                    <v-tooltip :text="vessel.status?.infoSentence">
                        <template v-if="vessel.states.acclimating" v-slot:activator="{ props }">
                            <v-chip v-bind="props" class="mt-2" color="orange" variant="outlined" size="small"
                                >Acclimating
                                <v-btn
                                    size="x-small"
                                    color="red"
                                    density="compact"
                                    variant="text"
                                    icon="mdi-timer-off"
                                    title="Stop Acclimating"
                                    @click="stopAcclimating"
                                    class="ml-1"
                                ></v-btn
                            ></v-chip>
                        </template>
                    </v-tooltip>

                    <div class="status-meta mt-3">
                        <div class="status-meta-row">
                            <v-icon icon="mdi-clock-outline" size="14" class="mr-1" />
                            <span v-if="vessel?.status?.dates?.lastStatusUpdatedDate !== undefined">
                                <time-ago prepend="Updated" :date="vessel.status.dates.lastStatusUpdatedDate" :timezone-id="vesselTimezone" />
                            </span>
                            <span v-else>Never Updated</span>
                        </div>
                        <div class="status-meta-row">
                            <v-icon icon="mdi-clock-fast" size="14" class="mr-1" />
                            <span v-if="vessel?.status?.dates?.nextStatusUpdateDate !== undefined">
                                <time-ago prepend="Next" :date="vessel.status.dates.nextStatusUpdateDate" :timezone-id="vesselTimezone" />
                            </span>
                            <span v-else>No Update Scheduled</span>
                            <v-btn
                                @click="runTimedChemCheck"
                                :loading="runningTimedChemCheck"
                                variant="text"
                                color="primary"
                                size="x-small"
                                icon="mdi-update"
                                title="Run a Timed Chem Check Now"
                                class="ml-1"
                            />
                        </div>
                        <v-btn
                            :to="{ name: 'VesselChemCheckHistory', params: { accountId, vesselId } }"
                            variant="text"
                            color="primary"
                            size="small"
                            class="mt-1 px-0"
                            >View Chem Check History</v-btn
                        >
                    </div>
                </div>

                <!-- Health Checks (part of the persistent sidebar) -->
                <div class="health-checks-col">
                    <vessel-health-checks
                        :account-id="accountId"
                        :vessel-id="vesselId"
                        :vessel="vessel"
                        :vessel-timezone="vesselTimezone"
                        :spa-health="spaHealth"
                        @refresh="statusUpdated"
                    />
                </div>
            </aside>

            <!-- Right content: tabbed detail sections -->
            <div class="vessel-tabs">
                <Tabs v-model:value="activeTab">
                    <div class="vessel-tabs-bar">
                        <TabList>
                            <Tab value="overview">Overview</Tab>
                            <Tab value="water">
                                Water
                                <Badge v-if="waterActionCount > 0" :value="waterActionCount" severity="danger" class="ml-2" />
                            </Tab>
                            <Tab value="chemicals">Chemicals</Tab>
                            <Tab value="account">Account</Tab>
                        </TabList>

                        <!-- Vessel-level actions menu, right-aligned in the tab bar -->
                        <v-menu v-if="!isSharedVessel">
                            <template v-slot:activator="{ props }">
                                <v-btn
                                    class="vessel-actions-menu-btn"
                                    icon="mdi-dots-vertical"
                                    variant="text"
                                    size="small"
                                    density="comfortable"
                                    title="Vessel actions"
                                    v-bind="props"
                                ></v-btn>
                            </template>
                            <v-list>
                                <v-list-item v-if="!isAcclimating" @click="startAcclimating" button title="Start Acclimating">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-clock-start" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item v-else-if="isAcclimating" @click="stopAcclimating" button title="Stop Acclimating">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-timer-off" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item @click="showAddWaterReportModal = true" button title="Add Water Report">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-plus" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item button @click="showStatusJSON" title="View Status JSON">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button v-if="monitorId" @click="showConfigurationJSON" title="View Configuration JSON">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-drawing" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button @click="showVesselEntitlementsModal = true" title="View Vessel Entitlements">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button @click="compareVesselStatuses" title="Compare Vessel Statuses">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button @click="showChemCheckInfoModal = true" title="View Vessel Chem Check Info">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button @click="showVesselTestingControlsModal = true" title="Edit for Testing">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-bug" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item
                                    button
                                    :href="`https://us-east-1.console.aws.amazon.com/dynamodbv2/home?region=us-east-1#edit-item?table=vessel_status&itemMode=2&pk=${vesselId}&sk&route=ROUTE_ITEM_EXPLORER`"
                                    target="_aws"
                                    title="View Status on DynamoDB"
                                >
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-aws" class="mr-2" color="orange"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-list-item button @click="viewProtocolInfo" title="View Protocol Info">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-eye" class="mr-2"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-divider v-if="spaConfigId"></v-divider>
                                <v-list-item v-if="spaConfigId" button @click="openSpaConfigFiles" title="Browse Config Files">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-folder-open" class="mr-2" color="blue"></v-icon>
                                    </template>
                                </v-list-item>
                                <v-divider></v-divider>
                                <v-list-item button @click="showConfirmDeleteVessel" title="Delete Vessel">
                                    <template v-slot:prepend>
                                        <v-icon icon="mdi-delete" class="mr-2" color="red"></v-icon>
                                    </template>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </div>
                    <TabPanels>
                        <!-- OVERVIEW -->
                        <TabPanel value="overview">
                            <div class="vessel-info-panel">
                                <!-- Gecko Spa -->
                                <div
                                    v-if="vessel.monitor"
                                    class="info-row clickable"
                                    @click="router.push({ name: 'GeckoSpaStatus', params: { accountId, vesselId } })"
                                >
                                    <div class="info-row-icon" style="background: #316094">
                                        <img src="/gecko-logo-white.svg" alt="Gecko" style="width: 22px; height: 22px" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">Gecko Spa</div>
                                        <div class="info-row-sub">SN: {{ vessel.monitor.monitorId }}</div>
                                    </div>
                                    <v-menu>
                                        <template v-slot:activator="{ props: menuProps }">
                                            <v-btn icon="mdi-dots-vertical" variant="text" density="compact" size="small" v-bind="menuProps" @click.stop />
                                        </template>
                                        <v-list density="compact">
                                            <v-list-item @click.stop="clickedDetachGeckoSpa" title="Detach Spa">
                                                <template v-slot:prepend><v-icon icon="mdi-close-circle" class="mr-2" color="red" /></template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>

                                <!-- Analytics -->
                                <div
                                    v-if="showAnalyticsCard"
                                    class="info-row clickable"
                                    @click="router.push({ name: 'VesselAnalytics', params: { accountId, vesselId, monitorId: vessel?.monitor?.monitorId } })"
                                >
                                    <div class="info-row-icon" style="background: #4caf50">
                                        <v-icon icon="mdi-chart-line" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">Analytics</div>
                                    </div>
                                    <v-icon icon="mdi-chevron-right" size="18" color="grey" />
                                </div>

                                <!-- Vessel Info -->
                                <div class="info-row">
                                    <div class="info-row-icon" style="background: #385f73">
                                        <v-icon icon="mdi-hot-tub" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">{{ vessel.name }}</div>
                                        <div class="info-row-sub">
                                            {{ Number(vessel.volumeGallons).toLocaleString() }} gal · {{ vessel.protocolName }}
                                            <span v-if="vessel.oemId"> · {{ vessel.oemId }}</span>
                                        </div>
                                    </div>
                                    <v-menu>
                                        <template v-slot:activator="{ props: menuProps }">
                                            <v-btn icon="mdi-dots-vertical" variant="text" density="compact" size="small" v-bind="menuProps" />
                                        </template>
                                        <v-list density="compact">
                                            <v-list-item @click="showEditVessel = true" title="Edit Vessel">
                                                <template v-slot:prepend><v-icon icon="mdi-pencil" class="mr-2" /></template>
                                            </v-list-item>
                                            <v-list-item @click="showChangeMonitorInstallTypeModal = true" title="Change Monitor Install Type">
                                                <template v-slot:prepend><v-icon icon="mdi-swap-horizontal" class="mr-2" /></template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>

                                <!-- Location -->
                                <div v-if="vessel.location" class="info-row clickable" @click="navigateToGoogleMaps">
                                    <div class="info-row-icon" style="background: #1f7087">
                                        <v-icon icon="mdi-map-marker" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">
                                            {{ vessel.location.locality
                                            }}<span v-if="vessel.location.administrativeAreaLevel1">, {{ vessel.location.administrativeAreaLevel1 }}</span>
                                        </div>
                                        <div class="info-row-sub">
                                            {{ vessel.location.line1 }}
                                            <span v-if="vessel.location?.timeZone?.timeZoneId"> · {{ vessel.location.timeZone.timeZoneId }}</span>
                                        </div>
                                    </div>
                                    <v-menu>
                                        <template v-slot:activator="{ props: menuProps }">
                                            <v-btn icon="mdi-dots-vertical" variant="text" density="compact" size="small" v-bind="menuProps" @click.stop />
                                        </template>
                                        <v-list density="compact">
                                            <v-list-item @click.stop="showEditVessel = true" title="Edit Location">
                                                <template v-slot:prepend><v-icon icon="mdi-pencil" class="mr-2" /></template>
                                            </v-list-item>
                                            <v-list-item @click.stop="updateLocationGeo" title="Update Geo Location & Timezone">
                                                <template v-slot:prepend><v-icon icon="mdi-compass" class="mr-2" /></template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>

                                <!-- Monitor -->
                                <div v-if="vessel.monitor?.monitorId" class="info-row clickable" @click="navigateToMonitorDetails">
                                    <div class="info-row-icon" style="background: #952175">
                                        <v-icon icon="mdi-access-point" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">{{ vessel.monitor.monitorId }}</div>
                                        <div class="info-row-sub">
                                            <span v-if="vessel.monitor?.sensor">Sensor #{{ vessel.monitor.sensor.serialNumber }}</span>
                                            <span v-if="vessel.status?.dates?.lastMonitorCheckinDate">
                                                · checked in <time-ago :date="vessel.status.dates.lastMonitorCheckinDate" :timezone-id="vesselTimezone"
                                            /></span>
                                        </div>
                                    </div>
                                    <v-menu>
                                        <template v-slot:activator="{ props: menuProps }">
                                            <v-btn icon="mdi-dots-vertical" variant="text" density="compact" size="small" v-bind="menuProps" @click.stop />
                                        </template>
                                        <v-list density="compact">
                                            <v-list-item @click.stop="showDetachMonitor = true" title="Detach Monitor">
                                                <template v-slot:prepend><v-icon icon="mdi-close-circle" class="mr-2" color="red" /></template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                                <div v-else class="info-row">
                                    <div class="info-row-icon" style="background: #ccc">
                                        <v-icon icon="mdi-access-point-off" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title text-grey">No Monitor</div>
                                    </div>
                                    <v-btn
                                        icon="mdi-plus"
                                        variant="tonal"
                                        density="compact"
                                        size="small"
                                        color="purple"
                                        @click="showAttachMonitorDialog = true"
                                    />
                                </div>

                                <!-- Account Users -->
                                <div
                                    v-if="vessel.accountUsers?.length"
                                    class="info-row clickable"
                                    @click="router.push({ name: 'AccountDetails', params: { accountId } })"
                                >
                                    <div class="info-row-icon" style="background: #fb8c00">
                                        <v-icon icon="mdi-account-group" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">{{ vessel.accountUsers.length }} User{{ vessel.accountUsers.length > 1 ? 's' : '' }}</div>
                                        <div class="info-row-sub">
                                            <span v-for="(u, i) in vessel.accountUsers.slice(0, 3)" :key="i">
                                                {{ u.name || u.email }}<span v-if="i < Math.min(vessel.accountUsers.length, 3) - 1">, </span>
                                            </span>
                                            <span v-if="vessel.accountUsers.length > 3"> +{{ vessel.accountUsers.length - 3 }} more</span>
                                        </div>
                                    </div>
                                    <v-icon icon="mdi-chevron-right" size="18" color="grey" />
                                </div>

                                <!-- Subscription -->
                                <div v-if="!isSharedVessel && vessel.subscriptions?.length" class="info-row">
                                    <div class="info-row-icon" style="background: #6772e5">
                                        <v-icon icon="mdi-credit-card" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">{{ vessel.subscriptions[0].title }}</div>
                                        <div class="info-row-sub">
                                            {{ format(vessel.subscriptions[0].currentPeriodStartDate, 'M/d/yy') }} –
                                            {{ format(vessel.subscriptions[0].currentPeriodEndDate, 'M/d/yy') }}
                                            <subscription-status-chip :subscription="vessel.subscriptions[0]" class="ml-1" />
                                        </div>
                                    </div>
                                    <v-menu>
                                        <template v-slot:activator="{ props: menuProps }">
                                            <v-btn icon="mdi-dots-vertical" variant="text" density="compact" size="small" v-bind="menuProps" />
                                        </template>
                                        <v-list density="compact">
                                            <v-list-item
                                                v-if="vessel.subscriptions[0].stripeSubscriptionUrl"
                                                :href="vessel.subscriptions[0].stripeSubscriptionUrl"
                                                target="_stripe"
                                                title="View on Stripe"
                                            >
                                                <template v-slot:prepend
                                                    ><img src="@/assets/stripe-icon.svg" alt="Stripe" class="stripeIconSmall mr-2"
                                                /></template>
                                            </v-list-item>
                                            <v-list-item @click="viewCustomerPortalUrl" title="Customer Portal">
                                                <template v-slot:prepend
                                                    ><img src="@/assets/stripe-icon.svg" alt="Stripe" class="stripeIconSmall mr-2"
                                                /></template>
                                            </v-list-item>
                                            <v-list-item @click="viewPaymentMethodUpdateUrl" title="Update Payment Method">
                                                <template v-slot:prepend
                                                    ><img src="@/assets/stripe-icon.svg" alt="Stripe" class="stripeIconSmall mr-2"
                                                /></template>
                                            </v-list-item>
                                        </v-list>
                                    </v-menu>
                                </div>
                                <div v-else-if="!isSharedVessel" class="info-row">
                                    <div class="info-row-icon" style="background: #ccc">
                                        <v-icon icon="mdi-credit-card-off" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title text-grey">No Subscription</div>
                                    </div>
                                    <v-btn
                                        v-if="vessel.subscriptionUpgrades?.length"
                                        icon="mdi-plus"
                                        variant="tonal"
                                        density="compact"
                                        size="small"
                                        color="indigo"
                                        @click="showAddSubscriptionDialog = true"
                                    />
                                </div>

                                <!-- Account Health -->
                                <div
                                    v-if="
                                        !accountStore.loadingVesselAccountHealthInfo &&
                                        (accountStore.vesselAccountHealthInfo.mostRecentFeedback !== undefined ||
                                            accountStore.vesselAccountHealthInfo.openTicketCount > 0)
                                    "
                                    class="info-row clickable"
                                    @click="router.push({ name: 'AccountDetails', params: { accountId } })"
                                >
                                    <div class="info-row-icon" style="background: #385f73">
                                        <v-icon icon="mdi-heart-pulse" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">Account Health</div>
                                        <div class="info-row-sub">
                                            <span v-if="accountStore.vesselAccountHealthInfo.mostRecentFeedback">
                                                <v-rating
                                                    :model-value="accountStore.vesselAccountHealthInfo.mostRecentFeedback.rating"
                                                    readonly
                                                    density="compact"
                                                    size="x-small"
                                                    color="grey-lighten-1"
                                                    active-color="yellow-accent-4"
                                                    class="d-inline-flex"
                                                />
                                                {{ formatDistanceToNow(accountStore.vesselAccountHealthInfo.mostRecentFeedback.date, { addSuffix: true }) }}
                                            </span>
                                            <span v-if="accountStore.vesselAccountHealthInfo.openTicketCount > 0">
                                                · {{ accountStore.vesselAccountHealthInfo.openTicketCount }} open ticket{{
                                                    accountStore.vesselAccountHealthInfo.openTicketCount > 1 ? 's' : ''
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                    <v-icon icon="mdi-chevron-right" size="18" color="grey" />
                                </div>

                                <!-- Calendar -->
                                <div class="info-row clickable" @click="router.push({ name: 'VesselSchedulesCalendar', params: { accountId, vesselId } })">
                                    <div class="info-row-icon" style="background: #316094">
                                        <v-icon icon="mdi-calendar-clock" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title">Spa Calendar</div>
                                        <div class="info-row-sub">View operation schedule</div>
                                    </div>
                                    <v-icon icon="mdi-chevron-right" size="18" color="grey" />
                                </div>

                                <!-- Activity Sessions -->
                                <div
                                    class="info-row"
                                    :class="{ clickable: showActivityPanel }"
                                    :tabindex="showActivityPanel ? 0 : undefined"
                                    :role="showActivityPanel ? 'button' : undefined"
                                    @click="openActivitySessionDialog"
                                    @keydown.enter="openActivitySessionDialog"
                                    @keydown.space.prevent="openActivitySessionDialog"
                                >
                                    <div class="info-row-icon" :style="{ background: showActivityPanel ? '#2E7D32' : '#ccc' }">
                                        <v-icon icon="mdi-hot-tub" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <div class="info-row-title" :class="{ 'text-grey': !showActivityPanel }">Activity Sessions</div>
                                        <div class="info-row-sub" :class="{ 'text-grey': !showActivityPanel }">
                                            {{ showActivityPanel ? activityRowSub : activityPanelDisabledReason }}
                                        </div>
                                        <div v-if="showActivityPanel && activityLastFetchedAt" class="info-row-sub mt-1" style="color: #aaa">
                                            <v-icon icon="mdi-clock-outline" size="10" class="mr-1" />
                                            Updated {{ format(activityLastFetchedAt, 'h:mm:ss a') }}
                                        </div>
                                    </div>
                                    <v-chip v-if="showActivityPanel" :color="activityStatusColor" size="x-small" label style="flex-shrink: 0">{{
                                        activityStatusLabel
                                    }}</v-chip>
                                    <!-- Intentionally NOT gated by showActivityPanel: past sessions can still exist (and be worth
                             looking up) even when smart routines are currently disabled or the subscription has lapsed. -->
                                    <v-btn
                                        icon="mdi-history"
                                        size="x-small"
                                        variant="text"
                                        style="flex-shrink: 0"
                                        title="View Session History"
                                        @click.stop="router.push({ name: 'VesselSessionHistory', params: { accountId, vesselId } })"
                                    />
                                    <v-btn
                                        v-if="showActivityPanel"
                                        icon="mdi-refresh"
                                        size="x-small"
                                        variant="text"
                                        :loading="activityRefreshing"
                                        style="flex-shrink: 0"
                                        @click.stop="loadActivityPanel"
                                    />
                                </div>

                                <!-- Tags -->
                                <div class="info-row">
                                    <div class="info-row-icon" style="background: #7c3aed">
                                        <v-icon icon="mdi-tag-multiple" size="20" color="white" />
                                    </div>
                                    <div class="info-row-body">
                                        <ResourceTags resource-type="vessel" :resource-id="String(vesselId)" />
                                    </div>
                                </div>
                            </div>

                            <div class="mt-4">
                                <SupportNotesCard :account-id="accountId" :vessel-id="vesselId" :spa-name="vessel.name ?? `Vessel #${vesselId}`" />
                            </div>
                        </TabPanel>

                        <!-- WATER -->
                        <TabPanel value="water">
                            <div class="water-grid">
                                <Panel class="readings-panel">
                                    <template #header>
                                        <div class="panel-header-row">
                                            <span class="panel-title"><i class="pi pi-chart-bar"></i> Readings</span>
                                        </div>
                                    </template>
                                    <vessel-status-readings
                                        :monitor-readings="vessel.status?.monitorReadings"
                                        :report-readings="vessel.status?.reportReadings"
                                        :computed-readings="vessel.status?.computedReadings"
                                        :vessel-id="vesselId"
                                        :account-id="accountId"
                                        :vessel-timezone-id="vesselTimezone"
                                        :low-temp-mode="vessel.status?.lowTempMode"
                                        @updated-reading="updatedReading"
                                    />
                                    <div v-if="vessel.status?.dates?.eventHorizonDate" class="event-horizon-bar">
                                        <v-tooltip
                                            text="All samples before this date are ignored for readings & actions. This date is advanced when the user performs refills or detaches and attaches sensors."
                                        >
                                            <template v-slot:activator="{ props }">
                                                <span v-bind="props" class="event-horizon-text">
                                                    <i class="pi pi-clock"></i>
                                                    Event Horizon: {{ format(vessel.status?.dates?.eventHorizonDate, 'Pp') }}
                                                </span>
                                            </template>
                                        </v-tooltip>
                                    </div>
                                </Panel>

                                <Panel class="actions-panel">
                                    <template #header>
                                        <div class="panel-header-row">
                                            <span class="panel-title"><i class="pi pi-bolt"></i> Actions</span>
                                            <span class="panel-header-actions">
                                                <Button
                                                    @click="showActionStatusesModal = true"
                                                    label="Statuses"
                                                    icon="pi pi-list"
                                                    size="small"
                                                    severity="secondary"
                                                    text
                                                />
                                                <router-link :to="{ name: 'VesselActionHistory', params: { accountId, vesselId } }" class="no-underline">
                                                    <Button label="History" icon="pi pi-history" size="small" severity="secondary" text />
                                                </router-link>
                                            </span>
                                        </div>
                                    </template>

                                    <div v-if="actions && actions.length > 0" class="actions-list">
                                        <div v-for="action in actions" :key="action.type" class="action-item" @click="showAction(action)">
                                            <div class="action-icon-wrap">
                                                <img v-if="action.iconUrl" :src="action.iconUrl" class="action-icon" :alt="action.title" />
                                                <div v-else class="action-icon-placeholder"><i class="pi pi-exclamation-circle"></i></div>
                                            </div>
                                            <div class="action-body">
                                                <div class="action-title">{{ action.title }}</div>
                                                <div v-if="lastCompletedDateForActionType(action.type)" class="action-sub">
                                                    <time-ago
                                                        prepend="last completed"
                                                        :date="lastCompletedDateForActionType(action.type)"
                                                        :timezone-id="vesselTimezone"
                                                    />
                                                </div>
                                            </div>
                                            <i class="pi pi-chevron-right action-chevron"></i>
                                        </div>
                                    </div>
                                    <div v-else class="no-actions-msg">
                                        <i class="pi pi-check-circle" style="font-size: 1.2rem; color: #4caf50"></i>
                                        No Actions
                                    </div>

                                    <div v-if="suppressedActions && suppressedActions.length > 0" class="suppressed-section">
                                        <Divider />
                                        <div class="suppressed-label">Suppressed</div>
                                        <div v-for="action in suppressedActions" :key="action.actionType" class="action-item suppressed">
                                            <div class="action-icon-wrap">
                                                <img v-if="action.iconUrl" :src="action.iconUrl" class="action-icon" :alt="action.actionType" />
                                                <div v-else class="action-icon-placeholder suppressed-icon"><i class="pi pi-minus-circle"></i></div>
                                            </div>
                                            <div class="action-body">
                                                <div class="action-title">{{ action.actionType }}</div>
                                                <div class="action-sub">
                                                    <v-tooltip :text="suppressedActionEndDate(action)">
                                                        <template v-slot:activator="{ props }">
                                                            <span v-bind="props">{{ suppressedActionSubTitle(action) }}</span>
                                                        </template>
                                                    </v-tooltip>
                                                    <span v-if="lastCompletedDateForActionType(action.actionType)">
                                                        ,
                                                        <time-ago
                                                            prepend="last completed"
                                                            :date="lastCompletedDateForActionType(action.actionType)"
                                                            :timezone-id="vesselTimezone"
                                                        />
                                                    </span>
                                                </div>
                                            </div>
                                            <v-btn
                                                v-if="action.reason === 'snoozed'"
                                                icon="mdi-cancel"
                                                variant="text"
                                                size="small"
                                                color="grey"
                                                class="cancel-snooze-btn"
                                                @click="clickedCancelSnooze(action.actionType)"
                                            />
                                        </div>
                                    </div>
                                </Panel>
                            </div>
                        </TabPanel>

                        <!-- CHEMICALS -->
                        <TabPanel value="chemicals">
                            <Panel v-if="!isSharedVessel">
                                <template #header>
                                    <span class="panel-title"><i class="pi pi-flask"></i> Chemicals</span>
                                </template>
                                <vessel-chemicals
                                    :account-id="accountId"
                                    :vessel-id="vesselId"
                                    :vessel-volume="vessel.volumeGallons"
                                    :show-edit-controls="!isSharedVessel"
                                />
                            </Panel>
                            <div v-else class="tab-empty-msg">Chemicals are not available for shared vessels.</div>
                        </TabPanel>

                        <!-- ACCOUNT -->
                        <TabPanel value="account">
                            <Panel v-if="!isSharedVessel">
                                <template #header>
                                    <span class="panel-title"><i class="pi pi-share-alt"></i> Shared to Accounts</span>
                                </template>
                                <vessel-shares :account-id="accountId" :vessel-id="vesselId" />
                            </Panel>
                            <div v-else class="tab-empty-msg">This is a shared vessel.</div>
                        </TabPanel>
                    </TabPanels>
                </Tabs>
            </div>
        </div>

        <v-dialog v-model="showConfirmDeleteVesselModal" max-width="500px">
            <v-card>
                <v-card-title>Delete Vessel?</v-card-title>
                <v-card-text> This action is permanent and cannot be recovered. </v-card-text>
                <v-card-actions>
                    <v-btn color="grey-lighten-1" @click="showConfirmDeleteVesselModal = false">No, Cancel</v-btn>
                    <v-btn color="error" @click="deleteVessel">Yes, Delete Vessel</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <update-vessel-status-dialog
            v-if="vessel"
            :vessel-id="vessel.vesselId"
            v-model="showUpdateStatusDialog"
            @status-updated="statusUpdated"
        ></update-vessel-status-dialog>

        <add-water-report-modal :account-id="accountId" :vessel-id="vesselId" @water-report-added="waterReportAdded" v-model="showAddWaterReportModal" />

        <view-j-s-o-n-dialog v-model="showJsonDialog" :json="jsonViewerContent as (Record<string, unknown> | undefined)"></view-j-s-o-n-dialog>

        <v-snackbar v-model="showDeleteVesselSnackbar" multi-line :timeout="5000" color="red">
            <p>Oops! You must detach the monitor before you can delete this vessel.</p>
            <template v-slot:actions>
                <v-btn color="white" variant="text" @click="showDeleteVesselSnackbar = false">Close</v-btn>
            </template>
        </v-snackbar>

        <v-dialog v-model="showConfirmCancelSnoozeDialog" max-width="500px">
            <v-card>
                <v-card-title>Remove Snooze?</v-card-title>
                <v-card-text> This action is permanent and the user will be able to see this action after a refresh. </v-card-text>
                <v-card-actions>
                    <v-btn color="grey-lighten-1" @click="showConfirmCancelSnoozeDialog = false">No, Cancel</v-btn>
                    <v-btn color="error" @click="removeSnooze">Yes, Remove Snooze</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <action-details-modal v-model="showActionDetailsDialog" :account-id="accountId" :vessel-id="vesselId" :action-type="focusedActionType" />

        <edit-vessel-modal v-model="showEditVessel" @vessel-updated="vesselUpdated" />

        <detach-monitor-modal
            v-if="vessel?.monitor?.monitorId"
            :account-id="accountId"
            :vessel-id="vesselId"
            :monitor-id="vessel.monitor.monitorId"
            v-model="showDetachMonitor"
            @monitor-detached="monitorDetached"
        />

        <v-dialog v-model="showAttachMonitorDialog">
            <v-card :min-width="400">
                <v-card-title>Choose a Monitor</v-card-title>
                <v-card-text>
                    <choose-monitor @picked:monitor="pickedMonitorToAttach" />
                </v-card-text>
            </v-card>
        </v-dialog>

        <edit-monitor-install-type-modal
            :account-id="accountId"
            :vessel-id="vesselId"
            :install-type="vessel?.monitorInstallType ?? MonitorInstallTypesV1.Poolside"
            :fixture-version="vessel?.inlineFixtureVersion"
            v-model="showChangeMonitorInstallTypeModal"
            @changed-monitor-type="vesselUpdated"
        />

        <v-dialog v-model="showChemCheckResultDialog">
            <v-card title="Results">
                <v-card-text>
                    <div v-if="chemEngineResult?.statusCode !== undefined">Status Code: {{ chemEngineResult?.statusCode }}</div>
                    <div v-if="chemEngineResult?.error !== undefined">Error: {{ chemEngineResult.error }}</div>

                    <div v-if="chemEngineResult?.payload !== undefined && chemEngineResult.payload !== null && chemEngineResult.payload !== 'null'">
                        <h4>Payload:</h4>
                        <pre>{{ chemEngineResult.payload }}</pre>
                    </div>

                    <div v-if="chemEngineResult?.logs !== undefined">
                        <h4>Logs:</h4>
                        <pre>{{ chemEngineResult.logs }}</pre>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showConfirmDetachGeckoSpa" max-width="500px">
            <v-card>
                <v-card-title>Detach Gecko Spa?</v-card-title>
                <v-card-text>This will cause gecko spa controls to disappear in the user's app.</v-card-text>
                <v-card-actions>
                    <v-btn color="grey-lighten-1" @click="showConfirmDetachGeckoSpa = false">No, Cancel</v-btn>
                    <v-btn color="error" @click="detachGeckoSpa">Yes, Detach Spa</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <view-chem-check-info-modal v-model="showChemCheckInfoModal" :vessel-id="vesselId" />

        <vessel-testing-controls-modal :account-id="accountId" :vessel-id="vesselId" v-model="showVesselTestingControlsModal" @vessel-updated="vesselUpdated" />

        <v-dialog v-model="showActionStatusesModal">
            <v-card>
                <v-card-title>Action Statuses</v-card-title>
                <v-card-text>
                    <v-table>
                        <tbody>
                            <tr>
                                <th></th>
                                <th>Suppressed</th>
                                <th>Snoozed</th>
                                <td>Last Completed</td>
                            </tr>
                        </tbody>
                        <tbody>
                            <tr v-for="status of vessel?.actionStatuses" :key="status.actionType">
                                <th>
                                    <h4>{{ status.title }}</h4>
                                    <p style="font-weight: 300">{{ status.actionType }}</p>
                                </th>

                                <td>
                                    <v-chip v-if="status.suppressed" variant="flat" color="amber-darken-1" class="mr-2">Suppressed</v-chip>
                                    <time-ago v-if="status.suppressed" prepend="ends in" :date="status.suppressedUntilDate" :timezone-id="vesselTimezone" />
                                </td>

                                <td>
                                    <v-chip v-if="status.snoozed" variant="flat" color="amber-darken-1" class="mr-2">Snoozed</v-chip>
                                    <time-ago v-if="status.snoozed" prepend="ends in" :date="status.snoozedUntilDate" :timezone-id="vesselTimezone" />
                                    <v-btn
                                        v-if="status.snoozed"
                                        color="grey-lighten-1"
                                        icon="mdi-cancel"
                                        variant="text"
                                        class="cancelSnooze"
                                        @click="clickedCancelSnooze(status.actionType)"
                                    ></v-btn>
                                </td>

                                <td><time-ago :date="status.lastCompletedDate" :timezone-id="vesselTimezone" /></td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey-lighten-1" @click="showActionStatusesModal = false">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="showVesselEntitlementsModal" max-width="600">
            <v-card>
                <v-card-title>Vessel Entitlements</v-card-title>
                <v-card-text>
                    <div v-if="sortedVesselEntitlements && sortedVesselEntitlements.length > 0">
                        <v-chip
                            v-for="entitlement of sortedVesselEntitlements"
                            :key="entitlement.title"
                            :color="entitlement.status === 'enabled' ? 'green-darken-2' : 'red-darken-2'"
                            class="mr-2 mb-2"
                        >
                            <strong>{{ entitlement.title }}</strong
                            >: {{ entitlement.status }}
                        </v-chip>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey-lighten-1" @click="showVesselEntitlementsModal = false">Ok</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- <pre>{{ vessel }}</pre> -->

        <activity-session-dialog
            v-if="showActivityPanel"
            v-model="showActivitySessionDialog"
            :account-id="accountId"
            :vessel-id="vesselId"
            :current-temp-c="vessel?.status?.discElements?.tempC"
            @session-updated="loadActivityPanel"
        />

        <!-- Spa Config Files Dialog -->
        <SpaConfigFilesDialog v-if="spaConfigId" v-model="showSpaConfigFiles" :config-id="spaConfigId" @error="onSpaConfigFilesError" />
    </v-container>
</template>

<script setup lang="ts">
import { useApi } from '@/api'
import TimeAgo from '@/components/TimeAgo.vue'
import VesselStatusReadings from '@/components/VesselStatusReadings.vue'
import ActionDetailsModal from '@/components/actions/ActionDetailsModal.vue'
import VesselChemicals from '@/components/chemicals/VesselChemicals.vue'
import ViewJSONDialog from '@/components/debug/ViewJSONDialog.vue'
import ChooseMonitor from '@/components/monitors/ChooseMonitor.vue'
import SpaConfigFilesDialog from '@/components/monitors/SpaConfigFilesDialog.vue'
import SubscriptionStatusChip from '@/components/subscriptions/SubscriptionStatusChip.vue'
import DetachMonitorModal from '@/components/vessels/DetachMonitorModal.vue'
import EditMonitorInstallTypeModal from '@/components/vessels/EditMonitorInstallTypeModal.vue'
import EditVesselModal from '@/components/vessels/EditVesselModal.vue'
import VesselTestingControlsModal from '@/components/vessels/VesselTestingControlsModal.vue'
import AddWaterReportModal from '@/components/waterReports/AddWaterReportModal.vue'
import { camelCaseToSentence, formatDateForVesselTimezone, getErrorMessage, handleApiError, isApiNotFoundError, tempCToTempF } from '@/lib/utils'
import { useAccountStore } from '@/stores/account'
import { useBreadCrumbsStore } from '@/stores/breadCrumbs'
import { useGeneralStore } from '@/stores/general'
import { useVesselStore } from '@/stores/vessel'
import {
    ActionType,
    ActivityStatusV1IdEnum,
    JobExecutionStatesV1,
    MonitorInstallTypesV1,
    WaterStatusColor,
    type AccountVesselV3,
    type ActionDetailsV1,
    type GetAccountVesselSettingsSchemaV1,
    type GetUpcomingVesselActivityV1200Response,
    type MonitorJobsV1,
    type MonitorsSearchResultV1,
    type PutRunVesselStatusUpdateV1,
    type SuppressedActionV1,
    type VesselEntitlementsV1,
    type SpaConfigV1,
    type VesselStatusV1
} from '@geckoal/gecko-api-client'
import Message from 'primevue/message'
import Panel from 'primevue/panel'
import Divider from 'primevue/divider'
import Button from 'primevue/button'
import Tabs from 'primevue/tabs'
import TabList from 'primevue/tablist'
import Tab from 'primevue/tab'
import TabPanels from 'primevue/tabpanels'
import TabPanel from 'primevue/tabpanel'
import Badge from 'primevue/badge'
import { format, formatDistanceToNow } from 'date-fns'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import UpdateVesselStatusDialog from '../components/debug/UpdateVesselStatusDialog.vue'
import ViewChemCheckInfoModal from '../components/debug/ViewChemCheckInfoModal.vue'
import VesselShares from '../components/vessels/VesselShares.vue'
import ActivitySessionDialog from '@/components/activities/ActivitySessionDialog.vue'
import PremiumBanner from '@/components/PremiumBanner.vue'
import ResourceTags from '@/components/tags/ResourceTags.vue'
import SpaStateValidator from '@/components/vessels/SpaStateValidator.vue'
import VesselHealthChecks from '@/components/vessels/VesselHealthChecks.vue'
import SupportNotesCard from '@/components/notes/SupportNotesCard.vue'
import type { SpaHealthChecks } from '@/lib/vesselHealth'

const breadCrumbsStore = useBreadCrumbsStore()
const vesselStore = useVesselStore()
const accountStore = useAccountStore()
const router = useRouter()

const loading = ref(false)

// Which detail tab is active. 'overview' shows the vessel info + support notes,
// 'water' shows actions + readings, 'chemicals' and 'account' the remaining panels.
const activeTab = ref('overview')

const showActionDetailsDialog = ref(false)
const focusedActionType = ref<ActionType | undefined>()

const showUpdateStatusDialog = ref(false)
const showAddWaterReportModal = ref(false)
const showConfirmDeleteVesselModal = ref(false)
const showDeleteVesselSnackbar = ref(false)
const showJsonDialog = ref(false)
const showEditVessel = ref(false)
const showChemCheckInfoModal = ref(false)
const showChemCheckResultDialog = ref(false)
const showVesselTestingControlsModal = ref(false)
const showAttachMonitorDialog = ref(false)
const showDetachMonitor = ref(false)
const showChangeMonitorInstallTypeModal = ref(false)
const showAddSubscriptionDialog = ref(false)
const showActionStatusesModal = ref(false)
const showVesselEntitlementsModal = ref(false)
const showActivitySessionDialog = ref(false)
const vesselSettings = ref<GetAccountVesselSettingsSchemaV1 | undefined>()
const upcomingActivity = ref<GetUpcomingVesselActivityV1200Response | null>(null)
const activityLastFetchedAt = ref<Date | null>(null)
const activityRefreshing = ref(false)
const spaHealth = ref<SpaHealthChecks | undefined>()

const showConfirmCancelSnoozeDialog = ref(false)
const actionTypeToCancelSnooze = ref<ActionType | undefined>()
const chemEngineResult = ref<PutRunVesselStatusUpdateV1 | undefined>()
const jsonViewerContent = ref<VesselStatusV1 | SpaConfigV1 | undefined>()
const spaConfig = ref<SpaConfigV1 | undefined>()
const monitorJobs = ref<MonitorJobsV1 | undefined>()

const FAILED_STATUSES = new Set([JobExecutionStatesV1.Failed, JobExecutionStatesV1.TimedOut, JobExecutionStatesV1.Rejected])

const lastFailedExecution = computed(() => {
    const executions = monitorJobs.value?.executions
    if (!executions?.length) return undefined
    const sorted = [...executions].sort((a, b) => {
        const dateA = a.lastUpdatedAtDate?.getTime() ?? 0
        const dateB = b.lastUpdatedAtDate?.getTime() ?? 0
        return dateB - dateA
    })
    const latest = sorted[0]
    return FAILED_STATUSES.has(latest.status) ? latest : undefined
})

const props = defineProps({
    accountId: {
        type: Number,
        required: true
    },
    vesselId: {
        type: Number,
        required: true
    }
})

const isSharedVessel = computed(() => {
    return vessel.value?.sharedVesselInfo !== undefined
})

const vessel = computed<AccountVesselV3 | undefined>(() => {
    return vesselStore.vessel
})

const vesselTimezone = computed(() => {
    return vessel.value?.location?.timeZone?.timeZoneId
})

const isAcclimating = computed(() => {
    return vessel.value?.states.acclimating === true
})

const showEngineeringOptions = computed(() => {
    return useGeneralStore().$state.showEngineeringOptions
})

const showAnalyticsCard = computed(() => {
    return vessel.value?.monitor && !!import.meta.env.VITE_ANALYTICS_SINGLE_VESSEL_URL
})

const isPremium = computed(() => {
    return vessel.value?.subscriptions?.some((s) => s.status === 'active' || s.status === 'trialing') ?? false
})

const premiumPlanTitle = computed(() => {
    const activeSub = vessel.value?.subscriptions?.find((s) => s.status === 'active' || s.status === 'trialing')
    return activeSub?.title ?? 'Active'
})

const vesselStatusClass = computed(() => {
    const color = vessel.value?.status?.discElements?.waterStatusColor
    if (color === WaterStatusColor.Blue) return 'status-blue'
    if (color === WaterStatusColor.Orange) return 'status-orange'
    if (color === WaterStatusColor.Red) return 'status-red'
    return 'status-grey'
})

const monitorId = computed(() => {
    if (vessel.value?.accountId !== props.accountId || vessel.value.vesselId !== props.vesselId) return undefined // timing issue, cached monitor is out of sync - lie for now
    return vessel.value?.monitor?.monitorId
})

const spaConfigId = computed(() => {
    return spaConfig.value?.metadata?.configurationId
})

const sortedVesselEntitlements = computed(() => {
    if (vessel.value === undefined) return undefined

    return Object.keys(vessel.value.entitlements)
        .map((key) => {
            const value = vessel.value?.entitlements[key as keyof VesselEntitlementsV1]
            return {
                title: camelCaseToSentence(key),
                status: value
            }
        })
        .sort((a, b) => a.title.localeCompare(b.title))
})

const smartRoutinesEnabled = computed(() => vesselSettings.value?.settings.features.smart_routines.enabled === true)

const hasValidSubscription = computed(() => vessel.value?.subscriptions?.some((s) => s.status === 'active' || s.status === 'trialing') ?? false)

const showActivityPanel = computed(() => smartRoutinesEnabled.value && hasValidSubscription.value)

// Explains why the panel above is showing its inactive state, so support isn't left guessing why a
// vessel has no session info — this mirrors account_vessel_settings.settings.features.smart_routines.
const activityPanelDisabledReason = computed(() => {
    if (!smartRoutinesEnabled.value) return 'Smart Routines disabled'
    return 'No active subscription'
})

function openActivitySessionDialog() {
    if (!showActivityPanel.value) return
    showActivitySessionDialog.value = true
}

const activityStatusColor = computed(() => {
    const id = upcomingActivity.value?.status.id
    if (id === ActivityStatusV1IdEnum.InProgress) return 'green'
    if (id === ActivityStatusV1IdEnum.Preparing) return 'blue'
    if (id === ActivityStatusV1IdEnum.Idle) return 'grey'
    return 'grey'
})

const ACTIVITY_STATUS_LABEL: Record<string, string> = {
    [ActivityStatusV1IdEnum.InProgress]: 'In Progress',
    [ActivityStatusV1IdEnum.Preparing]: 'Preparing',
    [ActivityStatusV1IdEnum.Idle]: 'Scheduled' // When it is planned session but not preparing yet, it is in idle status but we want to show it as scheduled to differentiate from idle with no upcoming session
}

const activityStatusLabel = computed(() => ACTIVITY_STATUS_LABEL[upcomingActivity.value?.status.id ?? ''] ?? 'Idle')

const activityRowSub = computed(() => {
    const a = upcomingActivity.value
    if (!a) return 'No upcoming session'
    const id = a.status.id
    const endAt = a.endAt as unknown as string | null
    if (id === ActivityStatusV1IdEnum.InProgress && endAt) {
        return `In progress · ends ${formatDistanceToNow(new Date(endAt), { addSuffix: true })}`
    }
    if (id === ActivityStatusV1IdEnum.Preparing) {
        return `Preparing · starts ${formatDistanceToNow(a.startAt, { addSuffix: true })}`
    }
    if (id === ActivityStatusV1IdEnum.Idle) {
        return `Next: ${format(a.startAt, 'EEE MMM d · h:mm a')}`
    }
    return 'No upcoming session'
})

onMounted(() => {
    _loadInfo()
})

watch([() => props.accountId, () => props.vesselId], () => {
    _loadInfo(true)
})

function _loadInfo(forceLoad = false, forceGeoUpdate = false) {
    _updateBreadCrumb() //  show vessel # version
    getVesselDetails(forceLoad, forceGeoUpdate)
    accountStore.loadVesselAccountHealthInfo(props.accountId, forceLoad)
}

const getVesselDetails = async (forceLoad = false, forceGeoUpdate = false) => {
    await vesselStore.loadVessel(props.accountId, props.vesselId, forceLoad, forceGeoUpdate)
    spaConfig.value = await getConfig() // load spa config if available
    _updateBreadCrumb() // update with vessel name
    getMonitorJobs()
    loadActivityPanel()
}

const loadActivityPanel = async () => {
    await handleApiError(async () => {
        vesselSettings.value = await useApi().getAccountVesselSettingsV1({
            accountId: props.accountId,
            vesselId: props.vesselId
        })
    })
    if (!showActivityPanel.value) return
    activityRefreshing.value = true
    await handleApiError(async () => {
        const result = await useApi().getUpcomingVesselActivityV1({
            accountId: props.accountId,
            vesselId: props.vesselId
        })
        upcomingActivity.value = result ?? null
        activityLastFetchedAt.value = new Date()
    })
    activityRefreshing.value = false
}

const getMonitorJobs = async () => {
    const mid = monitorId.value
    if (!mid) {
        monitorJobs.value = undefined
        return
    }
    try {
        monitorJobs.value = await useApi().getMonitorJobsV1({ monitorId: mid })
    } catch (e) {
        console.error('Error fetching monitor jobs', e)
    }
}

// Triggered by CTRL+D settings and deleting or expiring readings
const statusUpdated = async () => {
    _loadInfo(true)
}

const vesselUpdated = () => {
    getVesselDetails(true)
    showEditVessel.value = false
}

function _updateBreadCrumb() {
    breadCrumbsStore.$patch({
        items: _buildBreadCrumbItems()
    })
}

function _buildBreadCrumbItems() {
    return [
        {
            text: `Account #${props.accountId}`,
            to: {
                name: 'AccountDetails',
                params: {
                    accountId: props.accountId
                }
            }
        },
        {
            text: vessel.value?.name ?? `Vessel #${props.vesselId}`
        }
    ]
}

const navigateToGoogleMaps = () => {
    window.open(vessel.value?.map?.mapsUrl, '_googleMaps')
}

const updateLocationGeo = async () => {
    await vesselStore.loadVessel(props.accountId, props.vesselId, true, true)
}

const viewPaymentMethodUpdateUrl = async () => {
    handleApiError(async () => {
        const result = await useApi().postStripeUpdatePaymentMethodPortalSessionV1({
            accountId: props.accountId,
            postStripePaymentMethodUpdatePortalSessionV1Body: {}
        })
        window.open(result.stripePaymentMethodUpdatePortalUrl, '_blank')
    })
}

const viewCustomerPortalUrl = async () => {
    handleApiError(async () => {
        const result = await useApi().postStripePortalSessionV1({
            accountId: props.accountId
        })
        window.open(result.stripeBillingPortalUrl, '_blank')
    })
}

const pickedMonitorToAttach = async (monitor: MonitorsSearchResultV1) => {
    await handleApiError(
        async () => {
            if (monitor.vesselId && monitor.vesselId !== props.vesselId) throw new Error('This monitor is already attached to a vessel.')
            await useApi().setVesselMonitorV2({
                accountId: props.accountId,
                vesselId: props.vesselId,
                setVesselMonitorV1: {
                    monitorId: monitor.monitorId,
                    ownByAccountIfUnowned: true
                }
            })
            vesselUpdated()
        },
        async () => {
            showAttachMonitorDialog.value = false
        }
    )
}

const monitorDetached = () => {
    vesselUpdated()
}

const actions = computed(() => {
    if (!vessel.value) return undefined
    return vessel.value.status?.actions
})

const suppressedActions = computed(() => {
    if (!vessel.value) return undefined
    return vessel.value.status?.suppressedActions
})

// Count of outstanding actions, surfaced as a badge on the Water tab so support
// can see there's work to do without opening the tab.
const waterActionCount = computed(() => actions.value?.length ?? 0)

const suppressedActionSubTitle = (suppressedAction: SuppressedActionV1) => {
    const items: string[] = [] //suppressedAction.reason
    // if (suppressedAction.suppressedUntilDate) items.push(`for the next ${formatDistanceToNowStrict(suppressedAction.suppressedUntilDate)}`)
    items.push(suppressedAction.reasonDetails)
    return items.join(', ')
}

const lastCompletedDateForActionType = (actionType: ActionType) => {
    const matching = vessel.value?.actionStatuses.find((as) => as.actionType === actionType)
    return matching?.lastCompletedDate
}

const suppressedActionEndDate = (suppressedAction: SuppressedActionV1) => {
    if (suppressedAction.suppressedUntilDate === undefined) return 'Suppression has no end date'
    const vesselTimezoneId = vessel.value?.location?.timeZone?.timeZoneId
    return `Suppression ends at ${formatDateForVesselTimezone({ date: suppressedAction.suppressedUntilDate, vesselTimezoneId })}`
}

const showAction = async (action: ActionDetailsV1) => {
    focusedActionType.value = action.type
    showActionDetailsDialog.value = true
}

const updatedReading = () => {
    statusUpdated()
}

const showStatusJSON = () => {
    jsonViewerContent.value = vessel.value?.status
    showJsonDialog.value = true
}

const showConfigurationJSON = async () => {
    loading.value = true
    try {
        jsonViewerContent.value = await getConfig()
    } finally {
        loading.value = false
    }

    showJsonDialog.value = true
}

const getConfig = async () => {
    const monitorId = vessel.value?.monitor?.monitorId

    if (monitorId === undefined) {
        console.error('No monitorId')
        return
    }

    try {
        return await useApi().getAccountMonitorConfigurationV1({
            accountId: props.accountId,
            monitorId
        })
    } catch (error) {
        // A missing spa config returns 404 and is an expected, non-error case — tolerate it silently.
        if (isApiNotFoundError(error)) return undefined

        useGeneralStore().$patch({ globalErrorMessage: await getErrorMessage(error) })
        return undefined
    }
}

const compareVesselStatuses = () => {
    router.push({ name: 'CompareVesselStatuses', params: { accountId: props.accountId, vesselId: props.vesselId } })
}

const navigateToMonitorDetails = () => {
    router.push({ name: 'MonitorDetails', params: { monitorId: vessel.value?.monitor?.monitorId } })
}

const showSpaConfigFiles = ref(false)

const openSpaConfigFiles = () => {
    showSpaConfigFiles.value = true
}

const onSpaConfigFilesError = (message: string) => {
    console.error('Spa config files error:', message)
}

const startAcclimating = async () => {
    await handleApiError(async () => {
        const result = await useApi().putVesselStartAcclimatingV1({
            accountId: props.accountId,
            vesselId: props.vesselId,
            putVesselStartAcclimatingV1: {
                acclimatingMinutes: 30
            }
        })
        console.log('Started Acclimating', result)

        await _loadInfo(true)
    })
}

const stopAcclimating = async () => {
    await handleApiError(async () => {
        const result = await useApi().delVesselStopAcclimatingV1({
            accountId: props.accountId,
            vesselId: props.vesselId
        })
        console.log('Stopped Acclimating', result)

        await _loadInfo(true)
    })
}

const waterReportAdded = async () => {
    await _loadInfo(true)
}

const showConfirmDeleteVessel = () => {
    if (monitorId.value !== undefined) {
        showDeleteVesselSnackbar.value = true
        return
    }

    showConfirmDeleteVesselModal.value = true
}

const viewProtocolInfo = () => {
    router.push({ name: 'ProtocolInfo', params: { accountId: props.accountId, vesselId: props.vesselId } })
}

const deleteVessel = async () => {
    await handleApiError(async () => {
        const result = await useApi().removeAccountVesselV1({
            accountId: props.accountId,
            vesselId: props.vesselId
        })

        if (result.deleted) {
            console.log('Deleted account vessel', result.deleted)
            router.replace({ name: 'AccountDetails', params: { accountId: props.accountId } })
        } else {
            throw new Error('Failed to delete vessel')
        }
    })
}

const removeSnooze = async () => {
    const actionType = actionTypeToCancelSnooze.value
    if (actionType === undefined) return

    await handleApiError(
        async () => {
            const result = await useApi().delVesselActionSnoozeV1({
                accountId: props.accountId,
                vesselId: props.vesselId,
                actionType
            })

            if (result.cancelled) {
                console.log('Cancelled snooze: ', actionType)
            }

            _loadInfo(true)
        },
        async () => {
            showConfirmCancelSnoozeDialog.value = false
        }
    )
}

const clickedCancelSnooze = (actionType: ActionType) => {
    actionTypeToCancelSnooze.value = actionType
    showConfirmCancelSnoozeDialog.value = true
}

const runningTimedChemCheck = ref(false)

const runTimedChemCheck = () => {
    if (vessel.value === undefined) return

    runningTimedChemCheck.value = true
    const { accountId, vesselId } = vessel.value
    handleApiError(async () => {
        chemEngineResult.value = await useApi().putRunVesselStatusUpdateV1({
            accountId,
            vesselId
        })
        showChemCheckResultDialog.value = true
        runningTimedChemCheck.value = false
    })
}

const showConfirmDetachGeckoSpa = ref(false)

const clickedDetachGeckoSpa = (e: Event) => {
    e.preventDefault()
    showConfirmDetachGeckoSpa.value = true
    return false
}

const detachGeckoSpa = async () => {
    await handleApiError(
        async () => {
            await useApi().removeAccountVesselV1({
                accountId: props.accountId,
                vesselId: props.vesselId
            })
            _loadInfo(true)
        },
        async () => {
            showConfirmDetachGeckoSpa.value = false
        }
    )
}
</script>

<style lang="scss" scoped>
/* Panel overrides — tighter padding */
:deep(.p-panel-content) {
    padding: 12px 16px;
}

:deep(.p-panel-header) {
    padding: 8px 16px;
}

/* Panel styles */
.readings-panel,
.actions-panel {
    height: 100%;
}

.panel-header-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.panel-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #444;
}

.panel-header-actions {
    display: flex;
    gap: 2px;
}

/* Health checks column: card fills the space, options menu stays pinned top-right */
.health-checks-col {
    width: 100%;

    // Panel fills the full sidebar width.
    .health-checks-panel {
        width: 100%;
    }
}

// Tab bar row: tabs on the left, the vessel actions menu pinned to the far right.
.vessel-tabs-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    :deep(.p-tablist) {
        flex: 1 1 auto;
    }
}

.vessel-actions-menu-btn {
    flex: 0 0 auto;
}

.event-horizon-bar {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #eee;
}

.event-horizon-text {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: #888;
    cursor: help;
}

/* Actions */
.actions-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.action-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
        background: #f6f8ff;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    }

    &.suppressed {
        opacity: 0.6;
        background: #fafafa;
        cursor: default;

        &:hover {
            box-shadow: none;
        }
    }
}

.action-icon-wrap {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.action-icon {
    width: 32px;
    height: 32px;
    border-radius: 6px;
}

.action-icon-placeholder {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    background: #e3f2fd;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #1565c0;
    font-size: 1rem;

    &.suppressed-icon {
        background: #eee;
        color: #6b6b6b;
    }
}

.action-body {
    flex: 1;
    min-width: 0;
}

.action-title {
    font-weight: 600;
    font-size: 0.82rem;
    line-height: 1.3;
}

.action-sub {
    font-size: 0.72rem;
    color: #888;
    line-height: 1.3;
}

.action-chevron {
    color: #ccc;
    font-size: 0.75rem;
}

.no-actions-msg {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 0;
    font-size: 0.85rem;
    color: #666;
}

.suppressed-section {
    margin-top: 2px;
}

.suppressed-label {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #999;
    margin-bottom: 6px;
}

.cancel-snooze-btn {
    &:hover {
        color: red !important;
    }
}

/* Tabbed layout: persistent sidebar (status + health) on the left, tabbed content on the right */
.vessel-layout {
    display: flex;
    align-items: flex-start;
    gap: 24px;
}

.vessel-sidebar {
    flex: 0 0 340px;
    width: 340px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.vessel-tabs {
    flex: 1 1 0;
    min-width: 0;

    // Let tab panel content sit flush with the tab labels; add breathing room on top.
    :deep(.p-tabpanels) {
        background: transparent;
        padding: 16px 0 0;
    }

    :deep(.p-tablist-tab-list) {
        border-bottom: 1px solid #e0e0e0;
    }

    :deep(.p-tab) {
        font-size: 0.8rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
}

/* Water tab: actions above readings, stacked; each panel full width */
.water-grid {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .actions-panel {
        order: 1;
    }
    .readings-panel {
        order: 2;
    }
}

.tab-empty-msg {
    padding: 24px;
    color: #888;
    font-size: 0.9rem;
    text-align: center;
}

/* On narrow screens the sidebar drops above the tabs and goes full width */
@media (max-width: 960px) {
    .vessel-layout {
        flex-direction: column;
    }
    .vessel-sidebar {
        width: 100%;
        flex: 0 0 auto;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 16px;
    }
    .vessel-status-summary {
        width: 200px;
        flex: 0 0 200px;
    }
    .vessel-sidebar .health-checks-col {
        flex: 1 1 320px;
    }
}

/* Vessel status summary */
.vessel-status-summary {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    .status-header {
        width: 100%;
        border-radius: 12px;
        padding: 16px 14px;
        text-align: center;
        color: white;

        &.status-blue {
            background: linear-gradient(135deg, #2196f3, #1565c0);
        }
        &.status-orange {
            background: linear-gradient(135deg, #ff9800, #e65100);
        }
        &.status-red {
            background: linear-gradient(135deg, #f44336, #b71c1c);
        }
        &.status-grey {
            background: linear-gradient(135deg, #9e9e9e, #616161);
        }
    }

    .status-temp {
        font-size: 42px;
        font-weight: 300;
        line-height: 1;
    }

    .status-name {
        font-size: 0.8rem;
        font-weight: 500;
        margin-top: 3px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .status-actions-count {
        font-size: 0.75rem;
        font-weight: 400;
        opacity: 0.85;
        margin-top: 2px;
    }

    .status-meta {
        font-size: 0.75rem;
        color: #666;
        width: 100%;
    }

    .status-meta-row {
        display: flex;
        align-items: center;
        margin-bottom: 2px;
    }
}

/* Info panel grid */
.vessel-info-panel {
    flex: 1;
    min-width: 0;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    background: transparent;
    align-content: start;

    @media (max-width: 1400px) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media (max-width: 900px) {
        grid-template-columns: 1fr;
    }

    .info-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 10px 12px;
        background: white;
        border-radius: 8px;
        min-height: 50px;
        overflow: hidden;
        transition: background 0.15s, box-shadow 0.15s;
        border: 1px solid #e8e8e8;

        &.clickable {
            cursor: pointer;

            &:hover {
                background: #f6f8ff;
                box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
            }
        }
    }

    .info-row-icon {
        width: 34px;
        height: 34px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .info-row-body {
        flex: 1;
        min-width: 0;
    }

    .info-row-title {
        font-weight: 600;
        font-size: 0.82rem;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .info-row-sub {
        font-size: 0.72rem;
        color: #888;
        line-height: 1.3;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.adjustmentTable {
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: flex-start;
    width: 100%;

    h4 {
        font-weight: 400;
        text-transform: uppercase;
        letter-spacing: 1.2px;
        font-size: 14px;
        color: #555;
        margin: 0 0 10px 0;
    }

    h2 {
        display: inline-block;
        position: relative;
        font-size: 22px;
        font-weight: 600;
        margin: 0;
        color: #555;
    }

    h5 {
        margin: -2px 0 0 0;
        font-size: 13px;
        color: #aaa;
        font-weight: 400;
    }

    .adjustmentCell {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
    }

    .centerAdjustmentCell {
        h2 {
            font-size: 30px;
            color: #222;
        }
    }
}

.contentCard {
    margin: 20px;
}

.analyticsLogo {
    width: 20px;
    height: 20px;
    margin-left: 10px;
    position: absolute;
    left: 10px;
    bottom: 10px;
}

.stripeIconSmall {
    height: 14px;
}

.no-underline {
    text-decoration: none;
}

/* Premium indicator */
.premium-page {
    background: linear-gradient(180deg, #fdf8e8 0%, transparent 220px);
}
</style>
